import React, { useState } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, 
  Fab, Tooltip, Dialog, DialogTitle, DialogContent, 
  DialogActions, Button, Typography, useTheme,
  Grid, Paper, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';

const ThemeSelector = ({ currentTheme, onThemeChange }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event) => {
    onThemeChange(event.target.value);
  };

  // Add the new themes to the list
  const themes = [
    { value: 'vibrant', label: 'Vibrant Gradient', description: 'Colorful and energetic with smooth gradients' },
    { value: 'minimalist', label: 'Sleek Minimalist', description: 'Clean and professional with subtle interactions' },
    { value: 'corporate', label: 'Corporate Elegance', description: 'Professional and refined with corporate colors' },
    { value: 'materialYou', label: 'Material You', description: 'Modern and user-focused with Google\'s latest design system' },
    { value: 'sunset', label: 'Warm Sunset', description: 'Warm, energetic theme with orange-red gradient accents' },
    { value: 'ocean', label: 'Ocean Blue', description: 'Calm, professional blue theme with fluid, rounded elements' },
  ];

  return (
    <>
      <Tooltip title="Change Theme" placement="left">
        <Fab
          size="small"
          color="secondary"
          aria-label="theme"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 24,
            zIndex: 999,
          }}
        >
          <PaletteIcon />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Choose Application Theme</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select from our curated design themes to customize the look and feel of your application.
          </Typography>
          
          <RadioGroup
            value={currentTheme}
            onChange={handleChange}
          >
            <Grid container spacing={2}>
              {themes.map((option) => (
                <Grid item xs={12} sm={6} key={option.value}>
                  <Paper 
                    elevation={currentTheme === option.value ? 3 : 1}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: currentTheme === option.value 
                        ? 'primary.main' 
                        : 'transparent',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: currentTheme === option.value 
                          ? 'primary.main'
                          : 'divider',
                      }
                    }}
                    onClick={() => onThemeChange(option.value)}
                  >
                    <FormControlLabel 
                      value={option.value} 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight={500}>
                            {option.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          
          <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Theme: {themes.find(t => t.value === currentTheme)?.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Theme preference will be saved in your browser.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThemeSelector;